import React, { useEffect, useState } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PracticeAreaModal = ({ practiceArea, isOpen, onClose }) => {
	const [activeTab, setActiveTab] = useState('overview');

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
			setActiveTab('overview');
		} else {
			document.body.style.overflow = 'unset';
		}

		return () => {
			document.body.style.overflow = 'unset';
		};
	}, [isOpen]);

	if (!isOpen || !practiceArea) return null;

	const tabs = [
		{ id: 'overview', label: 'Overview', icon: 'FileText' },
		{ id: 'services', label: 'Services', icon: 'List' },
	];

	const renderTabContent = () => {
		switch (activeTab) {
			case 'overview':
				return (
					<div className='space-y-6'>
						<div>
							<h4 className='font-playfair font-bold text-lg text-primary mb-3'>
								About This Practice Area
							</h4>
							<div className='text-text-secondary font-source leading-relaxed mb-4'>
								{practiceArea?.fullDescription?.split('\n').map((line, index) => {
									if (line.trim().startsWith('•')) {
										return (
											<div key={index} className='mb-2 ml-4'>
												{line.trim()}
											</div>
										);
									}
									return (
										<div key={index} className={line.trim() ? 'mb-2' : 'mb-4'}>
											{line.trim()}
										</div>
									);
								})}
							</div>
						</div>
					</div>
				);

			case 'services':
				return (
					<div className='space-y-4'>
						<h4 className='font-playfair font-bold text-lg text-primary mb-4'>Our Services</h4>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							{practiceArea?.services?.map((service, index) => (
								<div key={index} className='flex items-start space-x-3 p-4 bg-surface rounded-lg'>
									<div className='w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1'>
										<Icon name='Check' size={16} color='white' strokeWidth={2.5} />
									</div>
									<div>
										<h5 className='font-source font-semibold text-primary mb-1'>
											{service?.title}
										</h5>
										<p className='text-sm text-text-secondary'>{service?.description}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop'>
			<div className='bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden modal-enter'>
				{/* Header */}
				<div className='flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-accent/5'>
					<div className='flex items-center space-x-4'>
						<div className='w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center shadow-md'>
							<Icon name={practiceArea?.icon} size={24} color='white' strokeWidth={2.5} />
						</div>
						<div>
							<h3 className='font-playfair font-bold text-xl text-primary'>
								{practiceArea?.title}
							</h3>
							<p className='text-sm text-text-secondary font-source'>{practiceArea?.tagline}</p>
						</div>
					</div>
					<Button
						variant='ghost'
						size='sm'
						onClick={onClose}
						iconName='X'
						iconSize={20}
						className='text-text-secondary hover:text-primary'
					/>
				</div>

				{/* Tabs */}
				<div className='flex overflow-x-auto border-b border-gray-200 bg-surface/50'>
					{tabs?.map((tab) => (
						<button
							key={tab?.id}
							onClick={() => setActiveTab(tab?.id)}
							className={`flex items-center space-x-2 px-6 py-3 text-sm font-source font-medium whitespace-nowrap transition-all duration-200 ${
								activeTab === tab?.id
									? 'text-primary border-b-2 border-primary bg-white'
									: 'text-text-secondary hover:text-primary hover:bg-white/50'
							}`}
						>
							<Icon name={tab?.icon} size={16} />
							<span>{tab?.label}</span>
						</button>
					))}
				</div>

				{/* Content */}
				<div className='p-6 overflow-y-auto max-h-[calc(90vh-200px)]'>{renderTabContent()}</div>

				{/* Footer */}
				<div className='flex items-center justify-end p-6 border-t border-gray-200 bg-surface/30'>
					<div className='flex items-center space-x-3'>
						<Button
							variant='outline'
							size='sm'
							iconName='Phone'
							iconPosition='left'
							iconSize={16}
							className='text-[#800020] border-[#800020] hover:bg-[#800020] hover:text-white'
						>
							Call (+234) 909-552-9557
						</Button>
						<a
							href='/contact'
							className='inline-flex items-center px-4 py-2 bg-[#800020] hover:bg-[#800020]/90 text-white rounded-lg font-source font-semibold text-sm transition-colors duration-200'
						>
							<Icon name='Calendar' size={16} className='mr-2' />
							Schedule a Consultation
						</a>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PracticeAreaModal;
